import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface SessionDetailsData {
  id: string;
  name: string;
  description: string;
  createdByTeacherId: string;
  createdByTeacherName: string;
  courseId: string | null;
  courseName: string | null;
  unitId: number | null;
  unitName: string | null;
  sessionDate: string;
  location: string;
  currentStudents: number;
  isActive: boolean;
  createdDate: string;
  modificationDate: string;
}

interface Course {
  id: string;
  name: string;
  grade: string;
  term: string;
  status: 'active' | 'inactive';
  price: number;
  modificationDate: string;
  description?: string;
  imageUrl?: string;
  isOpenToAll: boolean;
  teacherName?: string;
  subjectName?: string;
}

interface Unit {
  id: number;
  unitName: string;
  titel: string;
  active: boolean;
  order: number;
  enrollmentDate: string | null;
  creationDate: string;
  lessons: any[];
}

interface Teacher {
  id: string;
  fullName: string;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
}

interface SessionStudent {
  id: string;
  sessionId: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  studentBarcode: string;
  phoneNumber: string;
  parentPhone: string;
  academicYear: string;
  gradeName: string;
  attendedDate: string;
  registrationDate: string;
  active: boolean;
}

const SessionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isTeacher, isModerator, isSaeedAdmin, getTeacherId } = useUser();
  const canViewStudentCounts = isSaeedAdmin();
  
  const [sessionDetails, setSessionDetails] = useState<SessionDetailsData | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [sessionStudents, setSessionStudents] = useState<SessionStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'students'>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    createdByTeacherId: '',
    gradeId: '',
    courseId: '',
    unitId: '',
    sessionDate: '',
    location: '',
  });

  // Fetch session details
  const fetchSessionDetails = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await authFetch(apiUrl(`Session/${id}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSessionDetails(data.data);
        setFormData({
          name: data.data.name,
          description: data.data.description,
          createdByTeacherId: data.data.createdByTeacherId,
          gradeId: data.data.gradeId || '',
          courseId: data.data.courseId || '',
          unitId: data.data.unitId?.toString() || '',
          sessionDate: data.data.sessionDate ? new Date(data.data.sessionDate).toISOString().slice(0, 16) : '',
          location: data.data.location,
        });
      } else {
        throw new Error(data.message || 'Failed to fetch session details');
      }
    } catch (error) {
      console.error('Error fetching session details:', error);
      setError('Failed to load session details. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch teachers
  const fetchTeachers = async () => {
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data.map((teacher: any) => ({
          id: teacher.id,
          fullName: teacher.fullName
        })));
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  // Fetch grades
  const fetchGrades = async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    }
  };

  // Fetch courses for selected teacher and grade
  const fetchCourses = useCallback(async (teacherId?: string, gradeId?: string) => {
    try {
      const teacherIdParam = teacherId || (isTeacher() ? getTeacherId() : null);
      const gradeIdParam = gradeId;
      
      const params = new URLSearchParams();
      params.append('pageNumber', '1');
      params.append('pageSize', '1000');
      
      if (teacherIdParam) {
        params.append('TeacherId', teacherIdParam);
      }
      
      if (gradeIdParam) {
        params.append('GradeId', gradeIdParam);
      }
      
      const url = apiUrl(`Course/all?${params.toString()}`);
        
      const response = await authFetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        const courseData = data.data;
        setCourses(courseData.data.map((course: any) => ({
          id: course.id,
          name: course.courseName,
          grade: course.grade,
          term: course.term,
          status: course.active ? 'active' : 'inactive',
          price: course.price,
          modificationDate: course.modificationDate,
          description: course.description || '',
          imageUrl: course.imagePath || '',
          isOpenToAll: course.isOpenToAll || false,
          teacherName: course.teacherName || 'غير محدد',
          subjectName: course.subjectName || 'غير محدد',
        })));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, [isTeacher, getTeacherId]);

  // Fetch units for selected course
  const fetchCourseUnits = useCallback(async (courseId: string) => {
    if (!courseId) {
      setUnits([]);
      return;
    }

    try {
      const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUnits(data.data.units || []);
      }
    } catch (error) {
      console.error('Error fetching course units:', error);
      setUnits([]);
    }
  }, []);

  // Fetch session students
  const fetchSessionStudents = useCallback(async () => {
    if (!id) return;
    
    setStudentsLoading(true);
    try {
      const response = await authFetch(apiUrl(`Session/${id}/students`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Ensure data.data is an array
        const students = Array.isArray(data.data) ? data.data : [];
        setSessionStudents(students);
      } else {
        throw new Error(data.message || 'Failed to fetch session students');
      }
    } catch (error) {
      console.error('Error fetching session students:', error);
      setSessionStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [id]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Reset unit when course changes
      ...(name === 'courseId' && { unitId: '' }),
      // Reset course and unit when grade changes
      ...(name === 'gradeId' && { courseId: '', unitId: '' })
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.name || !formData.sessionDate) {
        alert('يرجى ملء جميع الحقول المطلوبة');
        return;
      }

      const response = await authFetch(apiUrl(`Session/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: id,
          name: formData.name,
          description: formData.description,
          gradeId: formData.gradeId || null,
          courseId: formData.unitId ? null : (formData.courseId || null),
          unitId: formData.unitId ? parseInt(formData.unitId) : null,
          sessionDate: formData.sessionDate,
          location: formData.location,
          isActive: sessionDetails?.isActive || true
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsEditing(false);
        fetchSessionDetails(); // Refresh session details
        alert('تم تحديث تفاصيل المحاضرة بنجاح');
      } else {
        alert('Failed to update session: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Failed to update session. Please try again later.');
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date for short display
  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchSessionDetails();
    fetchTeachers();
    fetchGrades();
  }, [id, fetchSessionDetails]);

  // Fetch courses when teacher changes
  useEffect(() => {
    if (formData.createdByTeacherId && isEditing) {
      fetchCourses(formData.createdByTeacherId, formData.gradeId);
    }
  }, [formData.createdByTeacherId, formData.gradeId, isEditing, fetchCourses]);

  // Fetch courses when grade changes
  useEffect(() => {
    if (formData.gradeId && isEditing) {
      const teacherId = isTeacher() ? getTeacherId() : formData.createdByTeacherId;
      fetchCourses(teacherId || undefined, formData.gradeId);
    } else if (isEditing && !formData.gradeId) {
      // Clear courses when grade is deselected
      setCourses([]);
    }
  }, [formData.gradeId, formData.createdByTeacherId, isEditing, isTeacher, getTeacherId, fetchCourses]);

  // Fetch units when course changes
  useEffect(() => {
    if (formData.courseId) {
      fetchCourseUnits(formData.courseId);
    } else {
      setUnits([]);
    }
  }, [formData.courseId, fetchCourseUnits]);

  // Fetch students when students tab is active
  useEffect(() => {
    if (activeTab === 'students' && id) {
      fetchSessionStudents();
    }
  }, [activeTab, id, fetchSessionStudents]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !sessionDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error || 'Session not found'}</div>
          <button
            onClick={() => navigate('/sessions')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            العودة إلى المحاضرات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                تفاصيل المحاضرة
              </h1>
              <p className="mt-2 text-gray-600">{sessionDetails.name}</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/sessions')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                العودة
              </button>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                           shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {isEditing ? 'إلغاء التعديل' : 'تعديل المحاضرة'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  تفاصيل المحاضرة
                </div>
              </button>
              <button
                onClick={() => setActiveTab('students')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'students'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  الطلاب المسجلين
                </div>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'details' && (
              <div>
                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          اسم المحاضرة
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          name="name"
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          المدرس
                        </label>
                        {isModerator() ? (
                          <select
                            name="createdByTeacherId"
                            value={formData.createdByTeacherId}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border rounded-lg"
                            required
                          >
                            <option value="">اختر المدرس</option>
                            {teachers.map(teacher => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.fullName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                            {sessionDetails.createdByTeacherName}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الصف الدراسي
                        </label>
                        <select
                          name="gradeId"
                          value={formData.gradeId}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        >
                          <option value="">اختر الصف الدراسي</option>
                          {grades.map(grade => (
                            <option key={grade.id} value={grade.id}>
                              {grade.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الدورة (اختياري)
                        </label>
                        <select
                          name="courseId"
                          value={formData.courseId}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          disabled={!formData.gradeId}
                        >
                          <option value="">اختر الدورة (اختياري)</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {formData.courseId && (
                        <div>
                          <label className="block text-gray-700 text-sm font-bold mb-2">
                            الوحدة (اختياري)
                          </label>
                          <select
                            name="unitId"
                            value={formData.unitId}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="">اختر الوحدة (اختياري)</option>
                            {units.map(unit => (
                              <option key={unit.id} value={unit.id}>
                                {unit.unitName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الموقع
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={handleInputChange}
                          name="location"
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          تاريخ المحاضرة
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.sessionDate}
                          onChange={handleInputChange}
                          name="sessionDate"
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        />
                      </div>

                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        وصف المحاضرة
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={handleInputChange}
                        name="description"
                        className="w-full px-3 py-2 border rounded-lg"
                        rows={4}
                      />
                    </div>

                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium transition-colors duration-200"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg 
                                 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                                 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          حفظ التغييرات
                        </div>
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          اسم المحاضرة
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {sessionDetails.name}
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          المدرس
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {sessionDetails.createdByTeacherName}
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الدورة
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {sessionDetails.courseName || 'غير محدد'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الوحدة
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {sessionDetails.unitName || 'غير محدد'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الموقع
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {sessionDetails.location}
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          تاريخ المحاضرة
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {formatDate(sessionDetails.sessionDate)}
                        </div>
                      </div>

                      {canViewStudentCounts && (
                        <div>
                          <label className="block text-gray-700 text-sm font-bold mb-2">
                            عدد الطلاب الحالي
                          </label>
                          <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                            {sessionDetails.currentStudents}
                          </div>
                        </div>
                      )}


                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          الحالة
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            sessionDetails.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {sessionDetails.isActive ? 'نشط' : 'غير نشط'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          تاريخ الإنشاء
                        </label>
                        <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                          {formatDate(sessionDetails.createdDate)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        وصف المحاضرة
                      </label>
                      <div className="px-3 py-2 border rounded-lg bg-gray-50 text-gray-700 min-h-[100px]">
                        {sessionDetails.description || 'لا يوجد وصف'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'students' && (
              <div>
                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">الطلاب المسجلين في المحاضرة</h3>
                    {canViewStudentCounts && (
                      <div className="text-sm text-gray-500">
                        إجمالي الطلاب: <span className="font-medium text-blue-600">{Array.isArray(sessionStudents) ? sessionStudents.length : 0}</span>
                      </div>
                    )}
                  </div>
                </div>

                {studentsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : !Array.isArray(sessionStudents) || sessionStudents.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      <p className="text-lg font-medium mb-2">لا يوجد طلاب مسجلين</p>
                      <p className="text-sm">لم يتم تسجيل أي طلاب في هذه المحاضرة بعد</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              اسم الطالب
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              البريد الإلكتروني
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              رقم الهاتف
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الصف الدراسي
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              تاريخ التسجيل
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              تاريخ الحضور
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الحالة
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Array.isArray(sessionStudents) && sessionStudents.map((student) => (
                            <tr key={student.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                      <span className="text-sm font-medium text-blue-600">
                                        {student.studentName.charAt(0)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mr-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {student.studentName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {student.studentBarcode}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{student.studentEmail}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{student.phoneNumber}</div>
                                {student.parentPhone && student.parentPhone !== student.phoneNumber && (
                                  <div className="text-xs text-gray-500">
                                    ولي الأمر: {student.parentPhone}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {student.gradeName}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatShortDate(student.registrationDate)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {student.attendedDate ? formatShortDate(student.attendedDate) : 'لم يحضر بعد'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  student.active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {student.active ? 'نشط' : 'غير نشط'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetails;
                     